import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Timer, MapPin, Activity, Clock, List, AlertTriangle, Check } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorkouts } from "@/hooks/useWorkouts";
import { WorkoutCompletionModal } from "@/components/WorkoutCompletionModal";
import { ShareWorkoutModal } from "@/components/ShareWorkoutModal";
import { ShareableWorkout } from "@/components/ShareWorkoutCard";
import { Analytics } from "@/lib/analytics";

interface Split {
  id: string;
  kilometer: number;
  time: string; // Format: MM:SS
}

export const RunningWorkout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { createWorkout, addExerciseToWorkout } = useWorkouts();
  
  // Workout details
  const [title, setTitle] = useState("");
  const [totalDistance, setTotalDistance] = useState("");
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [seconds, setSeconds] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [notes, setNotes] = useState("");
  
  // Splits tracking
  const [splits, setSplits] = useState<Split[]>([]);
  const [currentSplit, setCurrentSplit] = useState({ kilometer: "", minutes: "", seconds: "" });
  
  // Logging mode: 'quick' (total time only) or 'detailed' (splits)
  const [loggingMode, setLoggingMode] = useState<'quick' | 'detailed'>('quick');
  
  // Validation state
  const [timeMismatch, setTimeMismatch] = useState(false);
  const [distanceMismatch, setDistanceMismatch] = useState(false);
  
  const [saving, setSaving] = useState(false);
  
  // Completion modal
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [completedWorkoutData, setCompletedWorkoutData] = useState<{
    title: string;
    duration: number;
    completedSets?: number;
    averagePace?: string;
    distance?: number;
    workoutId?: string;
  } | null>(null);

  // Save workout data to localStorage whenever it changes
  useEffect(() => {
    const workoutData = {
      title,
      totalDistance,
      hours,
      minutes,
      seconds,
      difficulty,
      notes,
      splits,
      loggingMode,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('runningWorkoutData', JSON.stringify(workoutData));
  }, [title, totalDistance, hours, minutes, seconds, difficulty, notes, splits, loggingMode]);

  // Load saved workout data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('runningWorkoutData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const dataDate = new Date(parsed.timestamp);
        const now = new Date();
        const hoursDiff = Math.abs(now.getTime() - dataDate.getTime()) / (1000 * 60 * 60);
        
        if (hoursDiff < 12) {
          setTitle(parsed.title || '');
          setTotalDistance(parsed.totalDistance || '');
          setHours(parsed.hours || '');
          setMinutes(parsed.minutes || '');
          setSeconds(parsed.seconds || '');
          setDifficulty(parsed.difficulty || 'medium');
          setNotes(parsed.notes || '');
          setSplits(parsed.splits || []);
          setLoggingMode(parsed.loggingMode || 'quick');
        } else {
          localStorage.removeItem('runningWorkoutData');
        }
      } catch (error) {
        console.error('Error loading saved workout data:', error);
        localStorage.removeItem('runningWorkoutData');
      }
    }
  }, []);

  // Calculate total time from splits (in seconds)
  const calculateTotalTimeFromSplits = (): number => {
    if (splits.length === 0) return 0;
    
    return splits.reduce((total, split) => {
      const [minutes, seconds] = split.time.split(':').map(Number);
      return total + (minutes * 60) + seconds;
    }, 0);
  };

  // Calculate total distance from splits (in km)
  const calculateTotalDistanceFromSplits = (): number => {
    if (splits.length === 0) return 0;
    
    return splits.reduce((total, split) => total + split.kilometer, 0);
  };

  // Convert seconds to HH:MM:SS format
  const secondsToTimeString = (totalSeconds: number): { hours: string; minutes: string; seconds: string } => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return {
      hours: hours.toString(),
      minutes: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0')
    };
  };

  // Sync total time and distance from splits when splits change
  useEffect(() => {
    if (loggingMode === 'detailed' && splits.length > 0) {
      const totalSecondsFromSplits = calculateTotalTimeFromSplits();
      const timeFromSplits = secondsToTimeString(totalSecondsFromSplits);
      const totalDistanceFromSplits = calculateTotalDistanceFromSplits();
      
      // Auto-update total time from splits
      setHours(timeFromSplits.hours);
      setMinutes(timeFromSplits.minutes);
      setSeconds(timeFromSplits.seconds);
      setTimeMismatch(false);
      
      // Auto-update total distance from splits
      if (totalDistanceFromSplits > 0) {
        setTotalDistance(totalDistanceFromSplits.toFixed(2));
        setDistanceMismatch(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splits, loggingMode]);

  // Validate time and distance mismatch when both are manually entered
  useEffect(() => {
    if (loggingMode === 'detailed' && splits.length > 0) {
      // Validate time
      const totalSecondsFromSplits = calculateTotalTimeFromSplits();
      const totalSecondsFromInput = convertTimeToMinutes() * 60;
      
      // Allow 1 second difference for rounding
      const timeMismatchValue = Math.abs(totalSecondsFromSplits - totalSecondsFromInput) > 1;
      setTimeMismatch(timeMismatchValue);
      
      // Validate distance
      const totalDistanceFromSplits = calculateTotalDistanceFromSplits();
      const manualDistance = totalDistance ? parseFloat(totalDistance) : 0;
      
      // Allow 0.01 km difference for rounding
      const distanceMismatchValue = manualDistance > 0 && Math.abs(totalDistanceFromSplits - manualDistance) > 0.01;
      setDistanceMismatch(distanceMismatchValue);
    } else {
      setTimeMismatch(false);
      setDistanceMismatch(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, minutes, seconds, totalDistance, splits, loggingMode]);

  const addSplit = () => {
    if (!currentSplit.kilometer || (!currentSplit.minutes && !currentSplit.seconds)) {
      toast.error(t('running.manualLog.toast.fillSplitKmTime'));
      return;
    }

    const newSplit: Split = {
      id: crypto.randomUUID(),
      kilometer: parseFloat(currentSplit.kilometer),
      time: `${currentSplit.minutes || '0'}:${(currentSplit.seconds || '0').padStart(2, '0')}`
    };

    setSplits(prev => [...prev, newSplit].sort((a, b) => a.kilometer - b.kilometer));
    setCurrentSplit({ kilometer: "", minutes: "", seconds: "" });
    toast.success(t('running.manualLog.toast.splitAdded'));
    
    // Switch to detailed mode if adding splits
    if (loggingMode === 'quick') {
      setLoggingMode('detailed');
    }
  };

  const removeSplit = (id: string) => {
    setSplits(prev => prev.filter(s => s.id !== id));
  };

  const calculatePace = () => {
    // Use splits distance if in detailed mode, otherwise use manual distance
    let distance: number;
    if (loggingMode === 'detailed' && splits.length > 0) {
      distance = calculateTotalDistanceFromSplits();
    } else {
      distance = totalDistance ? parseFloat(totalDistance) : 0;
    }
    
    if (!distance || distance === 0) return "";
    
    // Use splits time if in detailed mode, otherwise use manual time
    let totalMinutes: number;
    if (loggingMode === 'detailed' && splits.length > 0) {
      const totalSecondsFromSplits = calculateTotalTimeFromSplits();
      totalMinutes = totalSecondsFromSplits / 60;
    } else {
      const h = parseInt(hours) || 0;
      const m = parseInt(minutes) || 0;
      const s = parseInt(seconds) || 0;
      totalMinutes = h * 60 + m + s / 60;
    }
    
    if (totalMinutes === 0) return "";
    
    const paceMinutes = totalMinutes / distance;
    const paceMin = Math.floor(paceMinutes);
    const paceSec = Math.round((paceMinutes - paceMin) * 60);
    
    return `${paceMin}:${paceSec.toString().padStart(2, '0')} /km`;
  };

  const getTotalTimeString = () => {
    const h = parseInt(hours) || 0;
    const m = parseInt(minutes) || 0;
    const s = parseInt(seconds) || 0;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const convertTimeToMinutes = () => {
    const h = parseInt(hours) || 0;
    const m = parseInt(minutes) || 0;
    const s = parseInt(seconds) || 0;
    return h * 60 + m + Math.round(s / 60);
  };

  const saveWorkout = async (retryCount = 0): Promise<void> => {
    if (!title.trim()) {
      toast.error(t('running.manualLog.toast.titleRequired'));
      return;
    }

    setSaving(true);
    try {
      // Track running workout started (if not already tracked)
      if (!localStorage.getItem('running_workout_started')) {
        Analytics.runningWorkoutStarted(title.trim());
        localStorage.setItem('running_workout_started', 'true');
      }
      
      // Calculate total time - prioritize splits if in detailed mode
      let totalMinutes: number;
      if (loggingMode === 'detailed' && splits.length > 0) {
        // Use splits as source of truth
        const totalSecondsFromSplits = calculateTotalTimeFromSplits();
        totalMinutes = Math.round(totalSecondsFromSplits / 60);
      } else {
        // Use manually entered total time
        totalMinutes = convertTimeToMinutes();
      }
      
      // Calculate total distance - prioritize splits if in detailed mode
      let finalDistance: number | undefined;
      if (loggingMode === 'detailed' && splits.length > 0) {
        // Use splits as source of truth
        finalDistance = calculateTotalDistanceFromSplits();
      } else {
        // Use manually entered distance
        finalDistance = totalDistance ? parseFloat(totalDistance) : undefined;
      }
      
      // Create the running workout
      const workoutData = {
        title: title.trim(),
        workout_type: 'running',
        difficulty_level: difficulty,
        duration_minutes: totalMinutes > 0 ? totalMinutes : undefined,
        notes: notes.trim() || undefined,
        completed_at: new Date().toISOString()
      };

      // Retry without short Promise.race timeouts — slow mobile networks were failing saves that still completed server-side
      let workout;
      let workoutError: unknown;
      const maxRetries = 4;

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        const result = await createWorkout(workoutData);
        workout = result.data;
        workoutError = result.error;
        if (!workoutError && workout) break;
        if (attempt < maxRetries - 1) {
          const delay = Math.pow(2, attempt) * 400;
          await new Promise((r) => setTimeout(r, delay));
          console.log(`[RunningWorkout] Retry workout create ${attempt + 2}/${maxRetries}`);
        }
      }
      
      if (workoutError || !workout) {
        console.error('[RunningWorkout] Failed to create workout after retries:', workoutError);
        throw new Error((workoutError as { message?: string } | null)?.message || t('running.manualLog.toast.createFailed'));
      }

      // Add running exercise with distance and splits data
      // Use same time calculation logic (prioritize splits if in detailed mode)
      let totalMinutesForExercise: number;
      if (loggingMode === 'detailed' && splits.length > 0) {
        const totalSecondsFromSplits = calculateTotalTimeFromSplits();
        totalMinutesForExercise = Math.round(totalSecondsFromSplits / 60);
      } else {
        totalMinutesForExercise = convertTimeToMinutes();
      }
      
      const runningExercise = {
        name: 'Running',
        category: 'Cardio' as const,
        distance_km: finalDistance,
        duration_seconds: totalMinutesForExercise > 0 ? totalMinutesForExercise * 60 : undefined,
        notes: splits.length > 0 ? `Splits: ${splits.map(s => `${s.kilometer}km - ${s.time}`).join(', ')}` : undefined,
        muscle_groups: ['legs', 'cardiovascular'],
        equipment: 'none'
      };

      let exerciseError: unknown;
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        const result = await addExerciseToWorkout(workout.id!, runningExercise);
        exerciseError = result.error;
        if (!exerciseError) break;
        if (attempt < maxRetries - 1) {
          const delay = Math.pow(2, attempt) * 400;
          await new Promise((r) => setTimeout(r, delay));
          console.log(`[RunningWorkout] Retry add exercise ${attempt + 2}/${maxRetries}`);
        }
      }
      
      if (exerciseError) {
        console.error('[RunningWorkout] Failed to add exercise after retries:', exerciseError);
        throw new Error((exerciseError as { message?: string } | null)?.message || t('running.manualLog.toast.addExerciseFailed'));
      }

      // Show completion modal instead of navigating directly
      // Use same time calculation for display
      let duration: number;
      if (loggingMode === 'detailed' && splits.length > 0) {
        const totalSecondsFromSplits = calculateTotalTimeFromSplits();
        duration = Math.round(totalSecondsFromSplits / 60);
      } else {
        duration = convertTimeToMinutes();
      }
      
      const pace = calculatePace();
      const dist = finalDistance;
      
      setCompletedWorkoutData({
        title: title.trim(),
        duration,
        completedSets: 1,
        averagePace: pace || undefined,
        distance: dist,
        workoutId: workout.id
      });
      setShowCompletionModal(true);
      
      // Track running workout completed (fix: use correct function signature - distance, duration, pace)
      Analytics.runningWorkoutCompleted(
        dist || 0,
        duration,
        pace || ''
      );
      localStorage.removeItem('running_workout_started');
      
      // Clear saved data after successful save
      localStorage.removeItem('runningWorkoutData');
    } catch (error: any) {
      console.error('[RunningWorkout] Error saving workout:', error);
      
      // Provide specific error messages
      const errorMessage = error?.message || t('running.manualLog.toast.saveFailed');
      
      if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        toast.error(t('common.networkError'));
      } else if (errorMessage.includes('timeout')) {
        toast.error(t('common.timeoutError'));
      } else {
        toast.error(errorMessage);
      }
      
      // Don't clear form on error - allow user to retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gradient-subtle p-4 pb-24">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/workouts')}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-normal">{t('running.manualLog.pageTitle')}</h1>
            <p className="text-muted-foreground">{t('running.manualLog.pageSubtitle')}</p>
          </div>
          {/* Chat with Arnold button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/chat", { state: { fromWorkout: true } })}
            className="border-primary/20 text-primary hover:bg-primary/10"
            title={t('running.manualLog.chatWithCoach')}
          >
            💬 {t('running.manualLog.coachButton')}
          </Button>
        </div>

        {/* Workout Details */}
        <Card className="bg-gradient-card border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              {t('running.manualLog.details.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">{t('running.manualLog.details.workoutTitleLabel')}</Label>
              <Input
                id="title"
                placeholder={t('running.manualLog.details.titlePlaceholder')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="bg-background/50"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="distance">{t('running.manualLog.details.distanceLabel')}</Label>
                <Input
                  id="distance"
                  type="text"
                  inputMode="decimal"
                  placeholder={t('running.manualLog.details.distanceExample')}
                  value={totalDistance}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setTotalDistance(value);
                      // If manually editing in detailed mode, mark as manual entry
                      if (loggingMode === 'detailed' && splits.length > 0) {
                        // Validation will catch mismatch
                      }
                    }
                  }}
                  className={`bg-background/50 h-12 text-lg ${
                    distanceMismatch ? 'border-destructive border-2' : ''
                  } ${loggingMode === 'detailed' && splits.length > 0 ? 'opacity-75' : ''}`}
                  disabled={loggingMode === 'detailed' && splits.length > 0}
                  title={loggingMode === 'detailed' && splits.length > 0 ? t('running.manualLog.details.autoFromSplitsHint') : ''}
                />
                {distanceMismatch && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {t('running.manualLog.details.distanceMismatch', { sumKm: calculateTotalDistanceFromSplits().toFixed(2) })}
                  </p>
                )}
                {loggingMode === 'detailed' && splits.length > 0 && !distanceMismatch && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Check className="h-3 w-3" /> {t('running.manualLog.details.distanceAutoCalculated', { count: splits.length, sumKm: calculateTotalDistanceFromSplits().toFixed(2) })}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="difficulty">{t('running.manualLog.details.difficultyLabel')}</Label>
                <Select value={difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger className="bg-background/50 h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">{t('running.manualLog.difficulty.easy')}</SelectItem>
                    <SelectItem value="medium">{t('running.manualLog.difficulty.medium')}</SelectItem>
                    <SelectItem value="hard">{t('running.manualLog.difficulty.hard')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t('running.manualLog.details.totalTimeLabel')}</Label>
                <Tabs value={loggingMode} onValueChange={(v) => setLoggingMode(v as 'quick' | 'detailed')} className="w-auto">
                  <TabsList className="h-8">
                    <TabsTrigger value="quick" className="text-xs px-3">
                      <Clock className="h-3 w-3 mr-1" />
                      {t('running.manualLog.details.loggingModeQuick')}
                    </TabsTrigger>
                    <TabsTrigger value="detailed" className="text-xs px-3">
                      <List className="h-3 w-3 mr-1" />
                      {t('running.manualLog.details.loggingModeDetailed')}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="00"
                    value={hours}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (/^\d*$/.test(value) && parseInt(value) < 100)) {
                        setHours(value);
                        // If manually editing in detailed mode, mark as manual entry
                        if (loggingMode === 'detailed' && splits.length > 0) {
                          // User is manually overriding - validation will catch mismatch
                        }
                      }
                    }}
                    className={`bg-background/50 h-12 text-center text-lg font-mono ${
                      timeMismatch ? 'border-destructive border-2' : ''
                    } ${loggingMode === 'detailed' && splits.length > 0 ? 'opacity-75' : ''}`}
                    disabled={loggingMode === 'detailed' && splits.length > 0}
                    title={loggingMode === 'detailed' && splits.length > 0 ? t('running.manualLog.details.autoFromSplitsHint') : ''}
                  />
                  <p className="text-xs text-center text-muted-foreground">{t('running.manualLog.details.labelHours')}</p>
                </div>
                <div className="space-y-1">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="00"
                    value={minutes}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (/^\d*$/.test(value) && parseInt(value) < 60)) {
                        setMinutes(value);
                      }
                    }}
                    className={`bg-background/50 h-12 text-center text-lg font-mono ${
                      timeMismatch ? 'border-destructive border-2' : ''
                    } ${loggingMode === 'detailed' && splits.length > 0 ? 'opacity-75' : ''}`}
                    disabled={loggingMode === 'detailed' && splits.length > 0}
                    title={loggingMode === 'detailed' && splits.length > 0 ? t('running.manualLog.details.autoFromSplitsHint') : ''}
                  />
                  <p className="text-xs text-center text-muted-foreground">{t('running.manualLog.details.labelMins')}</p>
                </div>
                <div className="space-y-1">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="00"
                    value={seconds}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (/^\d*$/.test(value) && parseInt(value) < 60)) {
                        setSeconds(value);
                      }
                    }}
                    className={`bg-background/50 h-12 text-center text-lg font-mono ${
                      timeMismatch ? 'border-destructive border-2' : ''
                    } ${loggingMode === 'detailed' && splits.length > 0 ? 'opacity-75' : ''}`}
                    disabled={loggingMode === 'detailed' && splits.length > 0}
                    title={loggingMode === 'detailed' && splits.length > 0 ? t('running.manualLog.details.autoFromSplitsHint') : ''}
                  />
                  <p className="text-xs text-center text-muted-foreground">{t('running.manualLog.details.labelSecs')}</p>
                </div>
              </div>
              
              {timeMismatch && (
                <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {t('running.manualLog.details.timeMismatch')}
                  </p>
                </div>
              )}
              
              {loggingMode === 'detailed' && splits.length > 0 && !timeMismatch && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Check className="h-3 w-3" /> {t('running.manualLog.details.timeAutoCalculated', { count: splits.length })}
                </p>
              )}
            </div>

            {totalDistance && (hours || minutes || seconds) && (
              <div className="p-3 bg-accent/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('running.manualLog.details.averagePace')}</span>
                  <span className="text-lg font-semibold text-accent">{calculatePace()}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Kilometer Splits */}
        <Card className={`bg-gradient-card border-0 ${loggingMode === 'quick' ? 'opacity-60' : ''}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              {t('running.manualLog.splits.title')}
              {loggingMode === 'quick' && (
                <Badge variant="outline" className="ml-auto text-xs">{t('running.manualLog.splits.switchToDetailed')}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className={`space-y-4 ${loggingMode === 'quick' ? 'pointer-events-none' : ''}`}>
            <div className="grid grid-cols-[1fr,1fr,1fr,auto] gap-2 items-end">
              <div className="space-y-1">
                <Label className="text-xs">{t('running.manualLog.splits.kmShort')}</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="1"
                  value={currentSplit.kilometer}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setCurrentSplit(prev => ({ ...prev, kilometer: value }));
                    }
                  }}
                  className="bg-background/50"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('running.manualLog.splits.minShort')}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="5"
                  value={currentSplit.minutes}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (/^\d*$/.test(value) && parseInt(value) < 60)) {
                      setCurrentSplit(prev => ({ ...prev, minutes: value }));
                    }
                  }}
                  className="bg-background/50 text-center"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('running.manualLog.splits.secShort')}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="30"
                  value={currentSplit.seconds}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (/^\d*$/.test(value) && parseInt(value) < 60)) {
                      setCurrentSplit(prev => ({ ...prev, seconds: value }));
                    }
                  }}
                  className="bg-background/50 text-center"
                />
              </div>
              <Button onClick={addSplit} size="icon" className="shrink-0">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {splits.length > 0 && (
              <div className="space-y-2">
                <Separator />
                <div className="grid gap-2">
                  {splits.map((split) => (
                    <div key={split.id} className="flex items-center justify-between p-2 bg-background/20 rounded-md">
                      <span className="text-sm">
                        <Badge variant="outline" className="mr-2">{split.kilometer}km</Badge>
                        {split.time}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSplit(split.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="bg-gradient-card border-0">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <Label htmlFor="notes">{t('running.manualLog.notes.label')}</Label>
              <Textarea
                id="notes"
                placeholder={t('running.manualLog.notes.placeholder')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-background/50 min-h-[100px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={() => saveWorkout()}
          disabled={saving || !title.trim()}
          className="w-full h-12 bg-gradient-primary text-black shadow-glow hover:shadow-lg transition-all duration-300"
        >
          {saving ? t('running.manualLog.save.saving') : t('running.manualLog.save.button')}
        </Button>
      </div>

      {/* Workout Completion Modal */}
      {completedWorkoutData && (
        <>
          <WorkoutCompletionModal
            isOpen={showCompletionModal}
            onClose={() => {
              setShowCompletionModal(false);
              setCompletedWorkoutData(null);
              navigate('/progress');
            }}
            onAnalyzeNow={() => {
              setShowCompletionModal(false);
              // Build payload BEFORE clearing state
              const workoutDataForAnalysis = {
                title: completedWorkoutData?.title || title.trim(),
                duration: completedWorkoutData?.duration ?? convertTimeToMinutes(),
                distance: completedWorkoutData?.distance ?? (totalDistance ? parseFloat(totalDistance) : undefined),
                pace: completedWorkoutData?.averagePace || (calculatePace() || undefined),
                difficulty,
                splits: splits.map(s => ({ kilometer: s.kilometer, time: s.time })),
                notes: notes.trim() || undefined,
                type: 'running',
                _ts: Date.now()
              };
              // Persist as fallback so Chat can always pick it up
              localStorage.setItem('pendingWorkoutAnalysis', JSON.stringify(workoutDataForAnalysis));

              setCompletedWorkoutData(null);
              // Pass workout data to chat for immediate processing as state (best effort)
              navigate('/chat', { state: { fromWorkout: true, workoutData: workoutDataForAnalysis } });
            }}
            onAnalyzeLater={() => {
              setShowCompletionModal(false);
              setCompletedWorkoutData(null);
              navigate('/workouts');
            }}
            onShare={() => {
              setShowCompletionModal(false);
              setShowShareModal(true);
            }}
            workoutTitle={completedWorkoutData.title}
            duration={completedWorkoutData.duration}
            completedSets={completedWorkoutData.completedSets}
            averagePace={completedWorkoutData.averagePace}
            distance={completedWorkoutData.distance}
          />

          <ShareWorkoutModal
            isOpen={showShareModal}
            onClose={() => {
              setShowShareModal(false);
              setShowCompletionModal(true);
            }}
            workout={{
              id: completedWorkoutData.workoutId || 'unknown',
              title: completedWorkoutData.title,
              duration_minutes: completedWorkoutData.duration,
              completed_at: new Date().toISOString(),
              workout_type: 'running',
              distance_km: completedWorkoutData.distance,
              average_pace: completedWorkoutData.averagePace,
            }}
          />
        </>
      )}
    </div>
  );
};
